import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from '../patches/CustomOrbitControls';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import * as global from '../data/global';
import * as THREE from 'three';
import * as Components from '../components/@index.js';
import { MuiMaterials } from "../@index";
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import CableIcon from '@mui/icons-material/Cable';
import Compass from '../assets/images/compass.svg';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useGesture } from '@use-gesture/react';
import axios from "axios";
import { CustomText3D } from "../3ds/_shared/CustomText3D";
import { useAuth } from "../provider/authProvider.js";
import { createApiEndpoint } from '../index.js';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ReplayIcon from '@mui/icons-material/Replay';
import BinStice from '../3ds/bin/stice/BinStice';
import SaveIcon from '@mui/icons-material/Save';

// Update label via ref - NO setState, so NO re-render of Bin3d/Canvas when getHeltecData fires every 5s
const Bin3dLastScanLabel = ({ getThemeColor, forceUpdate }) => {
    const labelRef = useRef(null);
    useEffect(() => {
        const updateLabel = () => {
            if (!global.gw.selectedSite?.isCableFocused && labelRef.current) {
                labelRef.current.textContent = global.gw.selectedSite?.lastScan ?? '';
            }
        };
        global.gw.selectedSite.onLastScanUpdate = updateLabel;
        if (labelRef.current) labelRef.current.textContent = global.gw.selectedSite?.lastScan ?? '';
        return () => { global.gw.selectedSite.onLastScanUpdate = null; };
    }, []);
    return (
        <MuiMaterials.InputLabel
            key={`bin3d-lastscan-${global.gw.theme}-${forceUpdate}`}
            sx={{ color: getThemeColor('#333333', '#f4cc8e'), fontSize: "16px" }}
        >
            <span ref={labelRef}>{global.gw.selectedSite?.lastScan ?? ''}</span>
        </MuiMaterials.InputLabel>
    );
};

export const Bin3d = ({ isHistoryLoading: propIsHistoryLoading, selectedBinId }) => {
    const { setToken, token, clearToken } = useAuth();
    const [isHistoryLoading, setIsHistoryLoading] = useState(propIsHistoryLoading !== undefined ? propIsHistoryLoading : true);
    const [binThermoLoading, setBinThermoLoading] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);
    const [saveViewModalIsSiteView, setSaveViewModalIsSiteView] = useState(false);
    const [saveViewModalApplyAllUsers, setSaveViewModalApplyAllUsers] = useState(false);
    const [saveViewModalAllBins, setSaveViewModalAllBins] = useState(false);
    const [saveViewModalSelectedBinIds, setSaveViewModalSelectedBinIds] = useState(new Set());
    const [controlsGeneration, setControlsGeneration] = useState(0);
    const onBinControlsAttached = useCallback(() => {
        setControlsGeneration((g) => g + 1);
    }, []);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
            // Update icon colors when theme changes
            const isLight = global.gw.theme === 'light';
            const brightColor = isLight ? "#333333" : "#f4cc8e";
            const dimmedColor = isLight ? "#33333361" : "#f4cc8e61";
            const dimmedColorRgba = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
            
            // Update all tvc icons
            const tvcs = window.document.getElementsByClassName("tvc");
            Array.from(tvcs).forEach(tvc => {
                const currentColor = tvc.style.color;
                if (currentColor && (currentColor.includes("#f4cc8e") || currentColor.includes("rgb(244"))) {
                    tvc.style.color = dimmedColor;
                } else if (currentColor && (currentColor.includes("#333333") || currentColor.includes("rgb(51"))) {
                    tvc.style.color = isLight ? dimmedColor : "#f4cc8e61";
                }
            });
            
            // Update cbi icon
            const cbis = window.document.getElementsByClassName("cbi");
            Array.from(cbis).forEach(cbi => {
                const currentColor = cbi.style.color;
                if (currentColor && (currentColor.includes("#f4cc8e") || currentColor.includes("rgb(244"))) {
                    cbi.style.color = brightColor;
                } else if (currentColor && (currentColor.includes("rgba(244"))) {
                    cbi.style.color = dimmedColorRgba;
                }
            });
            
            // Update cmi icon
            const cmis = window.document.getElementsByClassName("cmi");
            Array.from(cmis).forEach(cmi => {
                const currentColor = cmi.style.color;
                if (currentColor && (currentColor.includes("#f4cc8e") || currentColor.includes("rgb(244"))) {
                    cmi.style.color = brightColor;
                } else if (currentColor && (currentColor.includes("rgba(244"))) {
                    cmi.style.color = dimmedColorRgba;
                }
            });
            
            // Update civc icon (invalid cable visibility)
            const civcs = window.document.getElementsByClassName("civc");
            Array.from(civcs).forEach(civc => {
                const currentColor = civc.style.color;
                if (currentColor && (currentColor.includes("#f4cc8e") || currentColor.includes("rgb(244"))) {
                    civc.style.color = brightColor;
                } else if (currentColor && (currentColor.includes("rgba(244"))) {
                    civc.style.color = dimmedColorRgba;
                } else if (currentColor && (currentColor.includes("#333333") || currentColor.includes("rgb(51"))) {
                    // Keep bright color if it's currently bright
                    if (!currentColor.includes("rgba")) {
                        civc.style.color = isLight ? brightColor : "#f4cc8e";
                    }
                }
            });
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    // triggerGradesRerender: re-render so grades toggle (A+) works. Cable visibility stays no-op (not used in Bin3d).
    useEffect(() => {
        if (global.gw.selectedSite) {
            global.gw.selectedSite.triggerCableVisibilityRerender = () => {};
            global.gw.selectedSite.triggerGradesRerender = () => setForceUpdate(prev => prev + 1);
        }
        return () => global.clearCableNameHover?.();
    }, []);
    
    // Initialize invalid cable and grades button state on mount
    useEffect(() => {
        const civcs = window.document.getElementsByClassName("civc");
        if (civcs && civcs.length > 0) {
            const isLight = global.gw.theme === 'light';
            const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
            const dimmedColor = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
            const currentState = global.gw.selectedSite.isInvalidCablesOn;
            civcs[0].style.color = currentState ? brightColor : dimmedColor;
        }
        const cgis = window.document.getElementsByClassName("cgi");
        if (cgis && cgis.length > 0) {
            const isLight = global.gw.theme === 'light';
            const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
            const dimmedColor = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
            const gradesOn = global.gw.selectedSite?.isGradesOn !== false;
            cgis[0].style.color = gradesOn ? brightColor : dimmedColor;
        }
    }, [forceUpdate]);
    
    // Sync prop with local state
    useEffect(() => {
        if (propIsHistoryLoading !== undefined) {
            setIsHistoryLoading(propIsHistoryLoading);
        }
    }, [propIsHistoryLoading]);

    const gesture = useGesture({
        onDragStart: e => {
            global.gw.selectedSite.mouseClickStartTime = Date.now();
            // Match Site 3D: orbiting in 3D camera mode must not drop cable focus.
            // Clearing it here made the next Heltec refresh draw every cable again
            // while the cable dropdown still showed the selection.
            if (global.gw.selectedSite.canDrag && global.gw.selectedSite.isCableFocused) {
                global.gw.selectedSite.isCableFocused = false;
                global.gw.selectedSite.focusedCableId = null;
                if (global.gw.selectedSite.triggerGradesRerender) global.gw.selectedSite.triggerGradesRerender();
                global.restoreCableVisibility?.();
            }
            if (global.gw.selectedSite.canDrag) {
                // Store initial screen coordinates for panning
                global.gw.selectedSite.mouseStartScreenX = e.values[0];
                global.gw.selectedSite.mouseStartScreenY = e.values[1];
            }
        },
        onDrag: e => {
            if (global.gw.selectedSite.canDrag) {
                // Get camera and its orientation
                const camera = global.gw.selectedSite.orbitControls.object;
                const cameraDirection = new THREE.Vector3();
                camera.getWorldDirection(cameraDirection);
                
                // Create camera-relative vectors for panning
                const cameraRight = new THREE.Vector3();
                cameraRight.crossVectors(cameraDirection, camera.up).normalize();
                
                const cameraUp = new THREE.Vector3();
                cameraUp.crossVectors(cameraRight, cameraDirection).normalize();
                
                // Calculate mouse movement in screen space
                const mouseDeltaX = e.values[0] - global.gw.selectedSite.mouseStartScreenX;
                const mouseDeltaY = e.values[1] - global.gw.selectedSite.mouseStartScreenY;
                
                // Convert screen movement to world movement using camera orientation
                const panSpeed = 0.5; // Adjust this value to control pan sensitivity
                const worldDeltaX = mouseDeltaX * panSpeed;
                const worldDeltaY = -mouseDeltaY * panSpeed;
                
                // Apply movement in camera-relative directions
                const worldMovement = new THREE.Vector3();
                worldMovement.addScaledVector(cameraRight, worldDeltaX);
                worldMovement.addScaledVector(cameraUp, worldDeltaY);
                
                let s = global.gw.selectedSite.siteRef[0];
                s.position.set(
                    global.gw.selectedSite.offset.x + worldMovement.x,
                    global.gw.selectedSite.offset.y + worldMovement.y,
                    global.gw.selectedSite.offset.z + worldMovement.z
                );
            }
        },
        onDragEnd: e => {
            if (global.gw.selectedSite.canDrag) {
                let s = global.gw.selectedSite.siteRef[0];

                //global.setIsObjectSelected();
                if (!global.gw.selectedSite.isObjectSelected &&
                    e.elapsedTime > 400) {
                    if (global.gw.selectedSite.offset.x !== s.position.x ||
                        global.gw.selectedSite.offset.z !== s.position.z) {
                        global.gw.selectedSite.isOffsetChanged = true;
                    }
                }
                else {
                    //  Change camera lookat
                    //global.gw.selectedSite.orbitControls.currentLookAt.sub(s.position);
                    //global.gw.selectedSite.orbitControls.updateCamera(global.gw.selectedSite.orbitControls.currentLookAt);
                }

                global.gw.selectedSite.offset.x = s.position.x;
                global.gw.selectedSite.offset.y = s.position.y;
                global.gw.selectedSite.offset.z = s.position.z;

                global.gw.selectedSite.canDrag = false;
                if (global.gw.selectedSite.orbitControls) {
                    global.gw.selectedSite.orbitControls.enableRotate = true;
                }
                const isLight = global.gw.theme === 'light';
                const brightColor = isLight ? "#333333" : "#f4cc8e";
                const dimmedColor = isLight ? "#33333361" : "#f4cc8e61";
                const tvcs = window.document.getElementsByClassName("tvc");
                if (tvcs && tvcs.length >= 2) {
                    tvcs[0].style.color = dimmedColor;
                    tvcs[1].style.color = brightColor;
                }
            }

            if (global.gw.selectedSite.isObjectSelected) {
                global.saveBinCamera();
            }
            else {
                global.saveSiteCamera();
            }

            global.gw.selectedSite.mouseClickDownTime = Date.now() - global.gw.selectedSite.mouseClickStartTime;
        },
        onMouseDown: (state) => {
            if (state.event.button === 2) {
                let cameraPosition = 0;
                let cameraRotation = 0;
                let cameraAspect = 0;
                if (global.gw.selectedSite && global.gw.selectedSite.orbitControls && global.gw.selectedSite.orbitControls.object) {
                    cameraPosition = global.gw.selectedSite.orbitControls.object.position;
                    cameraAspect = global.gw.selectedSite.orbitControls.object.aspect;
                    cameraRotation = global.gw.selectedSite.orbitControls.object.rotation;
                }

                let rect = state.event.target.getBoundingClientRect();
                let p = new THREE.Vector3();
                p.set(
                    ((state.event.clientX - rect.left) / rect.width) * 2 - 1,
                    ((rect.top - state.event.clientY) / rect.height) * 2 + 1,
                    1
                );

                let wp = global.getWorldPointUnderCursor(state.event);
                if (!wp) return;
                let canvas = `canvas: ${p.x.toFixed(2)} ${p.y.toFixed(2)} ${p.z.toFixed(2)}`;
                let world = `world: ${wp.x.toFixed(2)} ${wp.y.toFixed(2)} ${-wp.z.toFixed(2)}`;
                let groups = "";

                if (global.gw.selectedSite && global.gw.selectedSite.groupsRef && global.gw.selectedSite.groupsRef.length > 0) {
                    p.set(
                        wp.x,
                        wp.y,
                        -wp.z
                    );
                    let v = new THREE.Vector3();
                    global.gw.selectedSite.groupsRef.forEach(g => {
                        v.copy(g.position);
                        let lv = v.sub(p);
                        groups += `group: ${Math.round(-lv.x, 2)} ${Math.round(lv.y, 2)} ${Math.round(lv.z, 2)}\n`;
                    });
                }

                let cLookAt = `cLookAt: ${global.gw.selectedSite.orbitControls.currentLookAt.x.toFixed(2)} ${global.gw.selectedSite.orbitControls.currentLookAt.y.toFixed(2)} ${global.gw.selectedSite.orbitControls.currentLookAt.z.toFixed(2)}`;
                let fov = `cFov: ${global.gw.selectedSite.orbitControls.object.fov}`;
                let zoom = `cZoom: ${global.gw.selectedSite.orbitControls.object.zoom}`;
                let distance = `cDistance: ${global.gw.selectedSite.orbitControls.object.position.distanceTo(global.gw.selectedSite.orbitControls.currentLookAt)}`;
                alert(`cPosition: ${Math.round(cameraPosition.x)} ${Math.round(cameraPosition.y)} ${Math.round(cameraPosition.z)} \ncRotation: ${Math.round(cameraRotation.x * 180 / Math.PI)} ${Math.round(cameraRotation.y * 180 / Math.PI)} ${Math.round(cameraRotation.z * 180 / Math.PI)} \n${cLookAt}\n${distance}\n${fov}\n${zoom}\naspect: ${cameraAspect} \n${canvas} \n${world} \n${groups}`);
                         }
        },
        onPinchEnd: e => {
            // Only scale text and update camera distance if not focused on a cable
            if (!global.gw.selectedSite.isCableFocused) {
                global.scaleBinText();
                if (global.gw.selectedSite.isObjectSelected) {
                    //global.saveBinCamera();
                    //alert(global.gw.selectedSite.orbitControls.object.position.x + " " + global.gw.selectedSite.orbitControls.object.position.y + " " + global.gw.selectedSite.orbitControls.object.position.z);
                    global.gw.selectedSite.binCameraDistance = global.gw.selectedSite.orbitControls.object.position.distanceTo(global.gw.selectedSite.orbitControls.currentLookAt);
                }
            }
        },
        onWheelEnd: e => {
            // Only scale text and update camera distance if not focused on a cable
            if (!global.gw.selectedSite.isCableFocused) {
                global.scaleBinText();
                if (global.gw.selectedSite.isObjectSelected) {
                    //global.gw.selectedSite.zoomCount += e.movement[1] / 100;
                    global.gw.selectedSite.binCameraDistance = global.gw.selectedSite.orbitControls.object.position.distanceTo(global.gw.selectedSite.orbitControls.currentLookAt);
                }
            }
        },
    });

    // Cable click events removed - using dropdown selection only

    const focusCameraOnCable = (cable) => {
        if (!global.gw.selectedSite.orbitControls || !cable) {
            return;
        }

        const controls = global.gw.selectedSite.orbitControls;
        
        // Calculate the bounding box of the cable
        const box = new THREE.Box3();
        cable.meshes.forEach(mesh => {
            if (mesh.visible) {
                box.expandByObject(mesh);
            }
        });

        if (box.isEmpty()) {
            return;
        }

        // Get the center and size of the cable
        const center = new THREE.Vector3();
        const size = new THREE.Vector3();
        box.getCenter(center);
        box.getSize(size);

        // Calculate appropriate camera distance based on cable size
        const maxDimension = Math.max(size.x, size.y, size.z);
        const cameraDistance = maxDimension * 1.5; // Increased zoom - reduced multiplier from 3 to 1.5

        // Calculate camera position
        const camera = controls.object;
        const cameraDirection = new THREE.Vector3();
        camera.getWorldDirection(cameraDirection);

        // Position camera at an angle to the cable
        const angle = Math.PI / 4; // 45 degrees
        const height = cameraDistance * Math.sin(angle);
        const horizontalDistance = cameraDistance * Math.cos(angle);

        // Calculate camera position relative to cable center
        const cameraPosition = new THREE.Vector3(
            center.x + horizontalDistance,
            center.y + height,
            center.z + horizontalDistance
        );

        // Set a flag to prevent camera reset
        global.gw.selectedSite.isCableFocused = true;
        
        // Animate camera to new position
        controls.object.position.copy(cameraPosition);
        controls.updateCamera(center);
        
        // Save the camera position to prevent it from being reset
        global.gw.selectedSite.binCameraDistance = controls.object.position.distanceTo(center);
        
        // Scale text appropriately after a short delay to prevent interference
        setTimeout(() => {
            global.scaleBinText();
        }, 100);
    };

    useEffect(() => {
        if (global.gw.selectedSite.siteIndex < 0) return;
        //if (global.gw.siteInfos[global.gw.selectedSite.siteIndex].siteGuid !== "d25e8abf-e01a-4550-ab72-d24f2cf7f34e") return;

        global.gw.selectedSite.refreshId = setTimeout(() => {
            global.getHeltecData();
        }, global.gw.selectedSite.refreshRate);
    }, []);
    
    // Apply saved camera when this bin is selected and whenever live OrbitControls attach.
    // Selecting a cable re-renders the canvas and re-attaches controls. That must not
    // clear cable focus — the next Heltec refresh would turn every other cable back on.
    useEffect(() => {
        if (!global.gw.selectedSite?.isBinWindowView) return;
        if (global.gw.selectedSite.isCableFocused) {
            global.hideUnfocusedCables?.();
            return;
        }
        let cancelled = false;
        let applied = false;
        const run = async (reuseGen) => {
            if (cancelled || applied) return;
            const ok = await global.applyBinWindowCamera(
                selectedBinId ?? global.gw.selectedSite?.selectedBinId,
                { reuseGen }
            );
            if (ok && !cancelled) {
                applied = true;
            }
        };
        run(false);
        const delays = [50, 150, 300, 600, 1200, 2000, 3500, 5000];
        const ids = delays.map((ms) => setTimeout(() => run(true), ms));
        return () => {
            cancelled = true;
            ids.forEach(clearTimeout);
        };
    }, [selectedBinId, controlsGeneration]);

    // Bin-level meshes are driven by the same `grainwave-bin-levels-updated` path as Site3d.
    // BinWindow does not mount Site3d, so we register the listener here; otherwise switching
    // bins after `setSiteBinLevels` never calls `renderSiteBinLevels`.
    useEffect(() => {
        let cancelled = false;
        const renderWhenReady = () => {
            if (cancelled) return;
            let attempts = 0;
            const tick = () => {
                if (cancelled) return;
                const binsRef = global.gw.selectedSite?.binsRef;
                if (Array.isArray(binsRef) && binsRef.length > 0 &&
                    binsRef.some(b => b?.parent?.parent?.parent)) {
                    requestAnimationFrame(() => {
                        if (cancelled) return;
                        global.renderSiteBinLevels();
                    });
                    return;
                }
                if (attempts++ < 30) {
                    requestAnimationFrame(tick);
                }
            };
            requestAnimationFrame(tick);
        };

        const handleBinLevelsUpdated = () => renderWhenReady();
        const handleBinLevelHistoryUpdated = () => renderWhenReady();
        window.addEventListener("grainwave-bin-levels-updated", handleBinLevelsUpdated);
        window.addEventListener("grainwave-bin-level-history-updated", handleBinLevelHistoryUpdated);

        if (Array.isArray(global.gw.selectedSite?.binLevels) && global.gw.selectedSite.binLevels.length > 0) {
            renderWhenReady();
        }

        return () => {
            cancelled = true;
            window.removeEventListener("grainwave-bin-levels-updated", handleBinLevelsUpdated);
            window.removeEventListener("grainwave-bin-level-history-updated", handleBinLevelHistoryUpdated);
            global.clearSiteBinLevels();
        };
    }, []);

    // Subscribe to lastScan updates - use Bin3dLastScanLabel component so only the label re-renders,
    // NOT the Canvas (Canvas re-render was resetting camera to defaultCamera/site view when getHeltecData timer fired)

    // Cleanup useEffect - runs when component unmounts
    useEffect(() => {
        return () => {
            // Clean up timers
            if (global.gw.selectedSite.refreshId) {
                clearTimeout(global.gw.selectedSite.refreshId);
                global.gw.selectedSite.refreshId = null;
            }
            if (global.gw.selectedSite.hmsTimerId) {
                clearInterval(global.gw.selectedSite.hmsTimerId);
                global.gw.selectedSite.hmsTimerId = null;
            }
            // Reset flash animation count
            global.gw.selectedSite.flashAnimationCount = 0;
            
            // Clear all flash animations
            if (global.clearAllFlashAnimations) {
                global.clearAllFlashAnimations();
            }
        };
    }, []);

    // WebGL context restoration
    useEffect(() => {
        const handleContextLost = (event) => {
            console.warn('WebGL context lost, preventing default');
            event.preventDefault();
        };
        
        const handleContextRestored = () => {
            // console.log('WebGL context restored');
            // Force re-render of the 3D scene
            window.location.reload();
        };
        
        const canvas = document.querySelector('canvas');
        if (canvas) {
            canvas.addEventListener('webglcontextlost', handleContextLost);
            canvas.addEventListener('webglcontextrestored', handleContextRestored);
        }
        
        return () => {
            if (canvas) {
                canvas.removeEventListener('webglcontextlost', handleContextLost);
                canvas.removeEventListener('webglcontextrestored', handleContextRestored);
            }
        };
    }, []);

    // WebGL context restoration
    useEffect(() => {
        const handleContextLost = (event) => {
            console.warn('WebGL context lost, preventing default');
            event.preventDefault();
        };
        
        const handleContextRestored = () => {
            // console.log('WebGL context restored');
            // Force re-render of the 3D scene
            window.location.reload();
        };
        
        const canvas = document.querySelector('canvas');
        if (canvas) {
            canvas.addEventListener('webglcontextlost', handleContextLost);
            canvas.addEventListener('webglcontextrestored', handleContextRestored);
        }
        
        return () => {
            if (canvas) {
                canvas.removeEventListener('webglcontextlost', handleContextLost);
                canvas.removeEventListener('webglcontextrestored', handleContextRestored);
            }
        };
    }, []);

    // Use a persistent camera instance so R3F doesn't re-apply defaultCamera config on re-renders
    // (Canvas re-render from getHeltecData/onLastScanUpdate was resetting camera to [0,300,0])
    const binCamera = useMemo(() => {
        if (!global.gw.selectedSite?.binCamera) {
            const cfg = global.gw.selectedSite?.defaultCamera || { fov: 40, near: 1, far: 5000, position: [0, 300, 0] };
            const cam = new THREE.PerspectiveCamera(cfg.fov || 40, 1, cfg.near || 1, cfg.far || 5000);
            cam.position.set(...(cfg.position || [0, 300, 0]));
            if (global.gw.selectedSite) global.gw.selectedSite.binCamera = cam;
            return cam;
        }
        return global.gw.selectedSite.binCamera;
    }, []);

    // Memoized - re-render when selectedBinId, isGradesOn, or isCableFocused changes (bin dropdown, grades toggle, cable selection)
    const Bin3dCanvasMemo = useMemo(() => React.memo(function Bin3dCanvasInner({ selectedBinId: _, isGradesOn, isCableFocused }) {
        const bind = gesture();
        const onPointerMove = (e) => {
            bind.onPointerMove?.(e);
        };
        const onPointerOut = (e) => {
            bind.onPointerOut?.(e);
            global.clearCableNameHover();
        };
        return (
            <Canvas
                key={`bin3d_canvas`}
                camera={binCamera}
                performance={{ min: 0.2 }}
                frameloop="always"
                dpr={[0.5, 1]}
                style={{
                    backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                    height: "100%",
                }}
                ref={el => {
                    if (!el) return;
                    global.gw.selectedSite.canvasRef = el;
                    global.gw.selectedSite.canvasRect = el.getBoundingClientRect();
                }}
                {...bind}
                onPointerMove={onPointerMove}
                onPointerOut={onPointerOut}
            >
                {global.gw && (!global.gw.geometries.An0 || !global.gw.geometries.AnPercent || !global.gw.geometries.AnDegree) ?
                    <group>
                        <CustomText3D 
                            scale={0.12}
                            side={THREE.DoubleSide}
                            position={new THREE.Vector3(0, 0, 0)}
                            meshProps={{
                                isHidden: true,
                                material:
                                    new THREE.MeshStandardMaterial({
                                        color: "#cccccc",
                                        transparent: true,
                                        opacity: 1,
                                        visible: false,
                                    })
                            }}
                        >
                            - 0123456789EO.%°FxABCDF
                        </CustomText3D>
                    </group>
                    :
                    null
                }

                <Components.GL site={global.gw.selectedSite}/>
                <ambientLight intensity={1.5}/>
                <directionalLight intensity={1.3} position={[0, 200, 0]} />
                <BinStice site={global.gw.selectedSite}/>
                <OrbitControls
                    key={`site3d_orbitControls`}
                    makeDefault
                    rotateSpeed={global.gw.selectedSite.orbitControls.rotateSpeed}
                    enablePan={false}
                    enableDamping={true}
                    dampingFactor={0.25}
                    enableRotate={global.gw.selectedSite.orbitControls.enableRotate}
                    ref={el => {
                        if (!el) return;
                        const previous = global.gw.selectedSite.orbitControls;
                        const isNew = previous !== el;
                        const wasDummy = !global.isLiveBinViewCamera(previous);
                        global.gw.selectedSite.orbitControls = el;
                        global.gw.selectedSite.orbitControls.minDistance = 0;
                        global.gw.selectedSite.orbitControls.maxDistance = Infinity;
                        global.gw.selectedSite.orbitControls.maxPolarAngle = Math.PI / 1.8;
                        global.gw.selectedSite.orbitControls.minPolarAngle = 0;
                        global.gw.selectedSite.orbitControls.maxAzimuthAngle = Infinity;
                        global.gw.selectedSite.orbitControls.minAzimuthAngle = -Infinity;
                        if (isNew || wasDummy) {
                            onBinControlsAttached();
                        }
                    }}
                    onEnd={() => {
                        if (global.gw.selectedSite?.isObjectSelected) {
                            global.saveBinCamera();
                        }
                    }}
                    name={"initialName"}
                    minPolarAngle={0}
                    maxPolarAngle={Math.PI / 1.8}
                />
                <EffectComposer disableNormalPass>
                    <Bloom luminanceThreshold={5} mipmapBlur />
                </EffectComposer>
            </Canvas>
        );
    }, (prev, next) => prev.selectedBinId === next.selectedBinId && prev.isGradesOn === next.isGradesOn && prev.isCableFocused === next.isCableFocused), []);

    // Check if site/layout data is loaded (don't block on history - date slider loads in background)
    if (!global.gw.selectedSite.layoutCables || !global.gw.selectedSite.isSiteInitialized) {
        return (
            <MuiMaterials.Box
                key={`bin3d-spinner-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    fontSize: "50px",
                    color: getThemeColor('#333333', 'rgb(244, 204, 142)'),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "fixed",
                    top: "0",
                    left: "0",
                    right: "0",
                    bottom: "0",
                    width: "100%",
                    height: "100%",
                    background: getThemeColor('rgba(255, 255, 255, 0.9)', 'rgba(0, 0, 0, 0.8)'),
                    zIndex: 9999,
                }}
            >
                <MuiMaterials.RotatingLines
                    strokeColor={getThemeColor('#333333', '#f4cc8e')}
                    strokeWidth="3"
                    animationDuration="0.72"
                    width="48"
                    visible={true}
                />
            </MuiMaterials.Box>
        );
    }

    return (
        <>
            <Bin3dCanvasMemo selectedBinId={selectedBinId} isGradesOn={global.gw.selectedSite?.isGradesOn !== false} isCableFocused={global.gw.selectedSite?.isCableFocused === true} />
            <div
                className="dateLabel"
                style={{
                    position: "absolute",
                    bottom: "0px",
                    right: "2px",
                    margin: "auto",
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    userSelect: "none",
                    pointerEvents: "none",
                    zIndex: "10",
                }}
            >
                <Bin3dLastScanLabel getThemeColor={getThemeColor} forceUpdate={forceUpdate} />
            </div>
            <div
                style={{
                    position: "absolute",
                    top: "66px",
                    left: "12px",
                    height: "calc(100vh - 72px)",
                }}
            >
                <Components.DateSlider />
            </div>
            
            <Components.Comparison /> 
            
            <MuiMaterials.Box
                sx={{
                    position: "absolute",
                    bottom: 0,
                    top: 30,
                    right: 0,
                    margin: "auto",
                    textAlign: "center",
                    flexDirection: "column",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    width: "28px",
                    height: "280px",
                }}
            >
                <Components.Thermo />
                <Components.GrainLevelGemini />
                <p style={{ margin: "0px" }}>
                        <MuiMaterials.Tooltip title="3D Pan Mode" arrow>
                            <MuiMaterials.Button
                                key={`bin3d-pan-${global.gw.theme}-${forceUpdate}`}
                                sx={{
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                    margin: "auto",
                                    maxWidth: "30px",
                                    maxHeight: "40px",
                                    minWidth: "30px",
                                    minHeight: "40px",
                                    '&:hover': {
                                        backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: getThemeColor('#33333361', '#f4cc8e61'),
                                    },
                                    '& .tvc': {
                                        color: getThemeColor('#33333361', '#f4cc8e61'),
                                    },
                                }}
                                onClick={e => {
                                    if (global.gw.selectedSite && global.gw.selectedSite.orbitControls) {
                                        global.gw.selectedSite.canDrag = true;
                                        global.gw.selectedSite.orbitControls.enableRotate = false;
                                    }
                                    let tvcs = window.document.getElementsByClassName("tvc");
                                    Array.from(tvcs).forEach(tvc => {
                                        tvc.style.color = global.gw.theme === 'light' ? "#33333361" : "#f4cc8e61";
                                    })
                                    e.currentTarget.children[0].style.color = global.gw.theme === 'light' ? "#333333" : "#f4cc8e";
                                }}
                            >
                                <ControlCameraIcon className="tvc" />
                            </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="3D Camera Mode" arrow>
                        <MuiMaterials.Button
                            key={`bin3d-camera-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                                '& .tvc': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                            }}
                            onClick={e => {
                                if (global.gw.selectedSite && global.gw.selectedSite.orbitControls) {
                                    global.gw.selectedSite.canDrag = false;
                                    global.gw.selectedSite.orbitControls.enableRotate = true;
                                }
                                let tvcs = window.document.getElementsByClassName("tvc");
                                Array.from(tvcs).forEach(tvc => {
                                    tvc.style.color = global.gw.theme === 'light' ? "#33333361" : "#f4cc8e61";
                                })
                                e.currentTarget.children[0].style.color = global.gw.theme === 'light' ? "#333333" : "#f4cc8e";
                            }}
                        >
                            <ThreeDRotationIcon className="tvc" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Save View" arrow>
                        <MuiMaterials.Button
                            key={`bin3d-saveview-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                color: getThemeColor('#33333361', '#f4cc8e61'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                            }}
                            onClick={async () => {
                                const isBinView = global.gw.selectedSite?.isBinWindowView;
                                if (isBinView) {
                                    setSaveViewModalIsSiteView(false);
                                    const currentBinId = parseInt(global.gw.selectedSite?.selectedBinId, 10) || 0;
                                    setSaveViewModalSelectedBinIds(currentBinId ? new Set([currentBinId]) : new Set());
                                    setSaveViewModalOpen(true);
                                } else {
                                    if (global.isAdminUser(global.gw.email)) {
                                        setSaveViewModalIsSiteView(true);
                                        setSaveViewModalApplyAllUsers(false);
                                        setSaveViewModalOpen(true);
                                    } else {
                                        const payload = global.buildSiteViewPayload();
                                        if (payload) {
                                            const ok = await createApiEndpoint().saveUserCustomerSiteSavedView("direct", null, "Site", 0, payload);
                                            if (ok) setForceUpdate(prev => prev + 1);
                                        }
                                    }
                                }
                            }}
                        >
                            <SaveIcon />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Reset to default view" arrow>
                        <MuiMaterials.Button
                            key={`bin3d-resetview-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                color: getThemeColor('#33333361', '#f4cc8e61'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                            }}
                            onClick={() => {
                                if (global.gw.selectedSite?.isBinWindowView && !global.gw.selectedSite?.isCableFocused) {
                                    global.resetBinViewToDefault();
                                    setForceUpdate(prev => prev + 1);
                                }
                            }}
                        >
                            <ReplayIcon />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Toggle Cable Visibility" arrow>
                        <MuiMaterials.Button
                            key={`bin3d-cable-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                                '& .cbi': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                            }}
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                
                                const currentState = global.gw.selectedSite.isCablesOff;
                                
                                if (currentState) {
                                    // Turn ON - show all cables in the current bin
                                    if (global.gw.selectedSite.cablesRef && global.gw.selectedSite.cablesRef.length > 0) {
                                        global.gw.selectedSite.cablesRef.forEach(cable => {
                                            if (cable.visible !== undefined) {
                                                cable.visible = true;
                                            }
                                            cable.meshes.forEach(mesh => {
                                                if (mesh.material && !mesh.isText && !mesh.isLevel) {
                                                    mesh.material.visible = true;
                                                    mesh.material.opacity = 1.0;
                                                    mesh.material.transparent = false;
                                                    mesh.material.needsUpdate = true;
                                                }
                                            });
                                            if (cable.children && cable.children.length > 0) {
                                                cable.children.forEach(child => {
                                                    if (child.material && !child.isLevel) {
                                                        child.material.visible = true;
                                                        child.material.opacity = 1.0;
                                                        child.material.transparent = false;
                                                        child.material.needsUpdate = true;
                                                    }
                                                });
                                            }
                                        });
                                    }
                                    global.gw.selectedSite.isCablesOff = false;
                                    
                                    // Update bin text colors for Bin3d (simpler logic - only one bin visible)
                                    // When cables are ON, show gray for all bins
                                    if (global.gw.selectedSite.binsRef) {
                                        global.gw.selectedSite.binsRef.forEach(b => {
                                            if (b.children && b.children.length > 0 && b.children[0] && b.children[0].children) {
                                                b.children[0].children.forEach(g => {
                                                    if (g && g.children && g.children.length > 0 && g.children[0] && g.children[0].material) {
                                                        g.children[0].material.color.set(global.gw.theme === 'light' ? "#878787" : "#898989");
                                                        g.children[0].material.emissive.set(global.gw.theme === 'light' ? "#878787" : "#898989");
                                                        g.children[0].material.needsUpdate = true;
                                                    }
                                                });
                                            }
                                        });
                                    }
                                    
                                    // Update button visual state
                                    let tvcs = window.document.getElementsByClassName("cbi");
                                    if (!tvcs || tvcs.length === 0) {
                                        const allButtons = window.document.querySelectorAll('button');
                                        for (let button of allButtons) {
                                            const tooltip = button.closest('[title="Toggle Cable Visibility"]');
                                            if (tooltip) {
                                                tvcs = [button];
                                                break;
                                            }
                                        }
                                    }
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                        tvcs[0].style.color = brightColor;
                                        tvcs[0].style.setProperty('color', brightColor, 'important');
                                    }
                                }
                                else {
                                    // Turn OFF - hide all cables
                                    if (global.gw.selectedSite.cablesRef && global.gw.selectedSite.cablesRef.length > 0) {
                                        global.gw.selectedSite.cablesRef.forEach(cable => {
                                            if (cable.visible !== undefined) {
                                                cable.visible = false;
                                            }
                                            cable.meshes.forEach(mesh => {
                                                if (mesh.material && !mesh.isText && !mesh.isLevel) {
                                                    mesh.material.visible = false;
                                                    mesh.material.needsUpdate = true;
                                                }
                                            });
                                            if (cable.children && cable.children.length > 0) {
                                                cable.children.forEach(child => {
                                                    if (child.material && !child.isLevel) {
                                                        child.material.visible = false;
                                                        child.material.needsUpdate = true;
                                                    }
                                                });
                                            }
                                        });
                                    }
                                    global.gw.selectedSite.isCablesOff = true;
                                    
                                    // Update bin text colors for Bin3d (simpler logic - only one bin visible)
                                    // When cables are OFF, show temperature colors for all bins
                                    if (global.gw.selectedSite.binsRef) {
                                        global.gw.selectedSite.binsRef.forEach(b => {
                                            if (b.children && b.children.length > 0 && b.children[0] && b.children[0].children) {
                                                // Get temperature color for this bin
                                                let temperatureList = global.gw.selectedSite.layoutSensors.filter(x => x.customerSiteLayoutBinId === b.binId)
                                                    .map(s => { return s.sensorValueNumber; });
                                                
                                                // Calculate max temperature and get color
                                                let filteredNumbers = temperatureList.filter(s => !isNaN(+s)).map(s => +s);
                                                let maxTemp = filteredNumbers.length > 0 ? Math.max(...filteredNumbers) : null;
                                                
                                                // Get color using the same function as the rest of the codebase
                                                let color = maxTemp !== null && global.getTemperatureColorString ? 
                                                    global.getTemperatureColorString(maxTemp) : "#55ff55";
                                                
                                                b.children[0].children.forEach(g => {
                                                    if (g && g.children && g.children.length > 0 && g.children[0] && g.children[0].material) {
                                                        g.children[0].material.color.set(color);
                                                        g.children[0].material.emissive.set(color);
                                                        g.children[0].material.needsUpdate = true;
                                                    }
                                                });
                                            }
                                        });
                                    }
                                    
                                    // Update button visual state
                                    let tvcs = window.document.getElementsByClassName("cbi");
                                    if (!tvcs || tvcs.length === 0) {
                                        const allButtons = window.document.querySelectorAll('button');
                                        for (let button of allButtons) {
                                            const tooltip = button.closest('[title="Toggle Cable Visibility"]');
                                            if (tooltip) {
                                                tvcs = [button];
                                                break;
                                            }
                                        }
                                    }
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        const dimmedColor = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                        tvcs[0].style.color = dimmedColor;
                                        tvcs[0].style.setProperty('color', dimmedColor, 'important');
                                    }
                                }
                                
                                // Force a complete re-render to make the cable visibility changes visible immediately
                                if (global.gw.selectedSite.orbitControls && global.gw.selectedSite.orbitControls.object) {
                                    global.gw.selectedSite.orbitControls.update();
                                }
                                
                                // Force a complete scene re-render
                                if (global.gw.selectedSite.renderer && global.gw.selectedSite.scene && global.gw.selectedSite.camera) {
                                    global.gw.selectedSite.renderer.render(global.gw.selectedSite.scene, global.gw.selectedSite.camera);
                                }
                                
                                // Force Three.js to update all materials
                                if (global.gw.selectedSite.scene) {
                                    global.gw.selectedSite.scene.traverse((object) => {
                                        if (object.material) {
                                            object.material.needsUpdate = true;
                                        }
                                    });
                                }
                                
                                // Force another render after material updates (using setTimeout like setAllCablesOn does)
                                if (global.gw.selectedSite.renderer && global.gw.selectedSite.scene && global.gw.selectedSite.camera) {
                                    setTimeout(() => {
                                        global.gw.selectedSite.renderer.render(global.gw.selectedSite.scene, global.gw.selectedSite.camera);
                                    }, 10);
                                }
                                
                                // Additional forced re-render to ensure visibility changes are immediate
                                if (global.gw.selectedSite.renderer && global.gw.selectedSite.scene && global.gw.selectedSite.camera) {
                                    requestAnimationFrame(() => {
                                        global.gw.selectedSite.renderer.render(global.gw.selectedSite.scene, global.gw.selectedSite.camera);
                                    });
                                }
                                
                                // Force scene update
                                global.scaleBinText();
                            }}
                        >
                            <CableIcon className="cbi" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Toggle Invalid Cable Visibility" arrow>
                        <MuiMaterials.Button
                            key={`bin3d-invalid-cable-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                background: "transparent",
                                padding: "0px",
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                                '& .civc': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                            }}
                            onClick={e => {
                                if (!global.gw.selectedSite.isInvalidCablesOn) {
                                    // Currently OFF - turn ON (show invalid cables for current bin)
                                    global.setInvalidCables(true);
                                    
                                    // Update button visual state to show "ON" (bright)
                                    let tvcs = window.document.getElementsByClassName("civc");
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                    }

                                    global.gw.selectedSite.isInvalidCablesOn = true;
                                } else {
                                    // Currently ON - turn OFF (hide invalid cables)
                                    global.setInvalidCables(false);
                                    
                                    // Update button visual state to show "OFF" (dimmed)
                                    let tvcs = window.document.getElementsByClassName("civc");
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        tvcs[0].style.color = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                    }

                                    global.gw.selectedSite.isInvalidCablesOn = false;
                                }
                            }}
                        >
                            <p className="civc">-E-</p>
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Toggle Grade Visibility" arrow>
                        <MuiMaterials.Button
                            key={`bin3d-grades-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                background: "transparent",
                                padding: "0px",
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: global.gw.selectedSite?.isGradesOn !== false ? getThemeColor('#333333', '#f4cc8e') : getThemeColor('rgba(51, 51, 51, 0.38)', 'rgba(244, 204, 142, 0.38)'),
                                },
                                '& .cgi': {
                                    color: global.gw.selectedSite?.isGradesOn !== false ? getThemeColor('#333333', '#f4cc8e') : getThemeColor('rgba(51, 51, 51, 0.38)', 'rgba(244, 204, 142, 0.38)'),
                                },
                            }}
                            onClick={e => {
                                if (!global.gw.selectedSite.isGradesOn) {
                                    global.setGradesVisibility(true);
                                    let tvcs = window.document.getElementsByClassName("cgi");
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                    }
                                } else {
                                    global.setGradesVisibility(false);
                                    let tvcs = window.document.getElementsByClassName("cgi");
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        tvcs[0].style.color = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                    }
                                }
                            }}
                        >
                            <p className="cgi">A+</p>
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="History Slider" arrow>
                        <MuiMaterials.Button
                            key={`bin3d-history-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                color: getThemeColor('#33333361', '#f4cc8e61'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                border: "none",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                                '& .cmi': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                            }}
                            onClick={e => {
                                global.toggleHistorySlider();
                            }}
                        >
                            <CalendarMonthIcon className="cmi" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Comparison" arrow>
                        <MuiMaterials.Button
                            key={`bin3d-comparison-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                color: getThemeColor('#33333361', '#f4cc8e61'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                                '& .cmp': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                            }}
                            onClick={e => {
                                global.toggleComparison();
                            }}
                        >
                            <CompareArrowsIcon className="cmp" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
            </MuiMaterials.Box>
            <MuiMaterials.Modal
                open={saveViewModalOpen}
                onClose={() => {
                    setSaveViewModalOpen(false);
                    setSaveViewModalIsSiteView(false);
                    setSaveViewModalAllBins(false);
                    setSaveViewModalSelectedBinIds(new Set());
                }}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <MuiMaterials.Box
                    sx={{
                        backgroundColor: getThemeColor('#fff', '#2d2d2d'),
                        p: 2,
                        borderRadius: 1,
                        minWidth: 320,
                        maxWidth: saveViewModalIsSiteView ? 360 : 420,
                    }}
                >
                    <MuiMaterials.Typography variant="h6" sx={{ mb: 2, color: getThemeColor('#333', '#f4cc8e') }}>
                        {saveViewModalIsSiteView ? 'Save Site View' : 'Save Bin View'}
                    </MuiMaterials.Typography>
                    <MuiMaterials.FormGroup>
                        {!saveViewModalIsSiteView && (
                        <MuiMaterials.Box sx={{ mb: 1 }}>
                            <MuiMaterials.Typography variant="body2" sx={{ color: getThemeColor('#666', '#aaa'), mb: 0.5 }}>
                                Bins to apply to:
                            </MuiMaterials.Typography>
                            <MuiMaterials.FormControlLabel
                                control={
                                    <MuiMaterials.Checkbox
                                        checked={saveViewModalAllBins}
                                        onChange={(e) => {
                                            setSaveViewModalAllBins(e.target.checked);
                                            if (e.target.checked) {
                                                const allBinIds = new Set((global.gw.selectedSite?.layoutBins ?? []).map(lb => lb.binId ?? lb.customerSiteLayoutBinId).filter(Boolean));
                                                setSaveViewModalSelectedBinIds(allBinIds);
                                            } else {
                                                const currentBinId = parseInt(global.gw.selectedSite?.selectedBinId, 10) || 0;
                                                setSaveViewModalSelectedBinIds(currentBinId ? new Set([currentBinId]) : new Set());
                                            }
                                        }}
                                        sx={{ color: getThemeColor('#333', '#f4cc8e') }}
                                    />
                                }
                                label="All bins"
                                sx={{ color: getThemeColor('#333', '#ccc') }}
                            />
                            {global.gw.selectedSite?.layoutBins?.length > 0 && (
                                <MuiMaterials.Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: getThemeColor('#ddd', '#444'), borderRadius: 1, p: 1, mt: 0.5 }}>
                                    {global.gw.selectedSite.layoutBins.map((lb) => {
                                        const binId = lb.binId ?? lb.customerSiteLayoutBinId;
                                        const checked = saveViewModalSelectedBinIds.has(binId);
                                        return (
                                            <MuiMaterials.FormControlLabel
                                                key={binId}
                                                control={
                                                    <MuiMaterials.Checkbox
                                                        checked={checked}
                                                        onChange={(e) => {
                                                            setSaveViewModalSelectedBinIds(prev => {
                                                                const next = new Set(prev);
                                                                if (e.target.checked) next.add(binId);
                                                                else next.delete(binId);
                                                                return next;
                                                            });
                                                        }}
                                                        sx={{ color: getThemeColor('#333', '#f4cc8e') }}
                                                    />
                                                }
                                                label={lb.binName ?? `Bin ${binId}`}
                                                sx={{ color: getThemeColor('#333', '#ccc'), display: 'block', mb: -0.5 }}
                                            />
                                        );
                                    })}
                                </MuiMaterials.Box>
                            )}
                        </MuiMaterials.Box>
                        )}
                        {global.isAdminUser(global.gw.email) && (saveViewModalIsSiteView || saveViewModalAllBins || saveViewModalSelectedBinIds.size > 0) && (
                            <MuiMaterials.FormControlLabel
                                control={
                                    <MuiMaterials.Checkbox
                                        checked={saveViewModalApplyAllUsers}
                                        onChange={(e) => setSaveViewModalApplyAllUsers(e.target.checked)}
                                        sx={{ color: getThemeColor('#333', '#f4cc8e') }}
                                    />
                                }
                                label="Apply to all users (administrator)"
                                sx={{ color: getThemeColor('#333', '#ccc') }}
                            />
                        )}
                    </MuiMaterials.FormGroup>
                    <MuiMaterials.Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <MuiMaterials.Button
                            onClick={() => setSaveViewModalOpen(false)}
                            sx={{ color: getThemeColor('#333', '#f4cc8e') }}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            variant="contained"
                            disabled={!saveViewModalIsSiteView && saveViewModalSelectedBinIds.size === 0}
                            onClick={async () => {
                                if (saveViewModalIsSiteView) {
                                    const payload = global.buildSiteViewPayload();
                                    if (!payload) {
                                        setSaveViewModalOpen(false);
                                        setSaveViewModalIsSiteView(false);
                                        return;
                                    }
                                    const ok = saveViewModalApplyAllUsers
                                        ? await createApiEndpoint().saveUserCustomerSiteSavedViewForAllUsers("direct", null, "Site", 0, payload)
                                        : await createApiEndpoint().saveUserCustomerSiteSavedView("direct", null, "Site", 0, payload);
                                    setSaveViewModalOpen(false);
                                    setSaveViewModalIsSiteView(false);
                                    setSaveViewModalApplyAllUsers(false);
                                    if (ok) setForceUpdate(prev => prev + 1);
                                    return;
                                }
                                const payload = global.buildBinViewPayload(global.gw.selectedSite?.selectedBinId);
                                if (!payload) {
                                    setSaveViewModalOpen(false);
                                    return;
                                }
                                let ok = true;
                                const saveOne = saveViewModalApplyAllUsers
                                    ? (bid) => createApiEndpoint().saveUserCustomerSiteSavedViewForAllUsers("direct", null, "Bin", bid, payload)
                                    : (bid) => createApiEndpoint().saveUserCustomerSiteSavedView("direct", null, "Bin", bid, payload);
                                const layoutBinIds = new Set((global.gw.selectedSite?.layoutBins ?? []).map(lb => lb.binId ?? lb.customerSiteLayoutBinId).filter(Boolean));
                                const allSelected = saveViewModalSelectedBinIds.size === layoutBinIds.size && [...saveViewModalSelectedBinIds].every(id => layoutBinIds.has(id));
                                if (saveViewModalAllBins && allSelected) {
                                    ok = await saveOne(0);
                                } else {
                                    const binIds = saveViewModalSelectedBinIds.size > 0
                                        ? Array.from(saveViewModalSelectedBinIds)
                                        : [parseInt(global.gw.selectedSite?.selectedBinId, 10) || 0];
                                    for (const binId of binIds) {
                                        const r = await saveOne(binId);
                                        if (!r) ok = false;
                                    }
                                }
                                setSaveViewModalOpen(false);
                                setSaveViewModalApplyAllUsers(false);
                                setSaveViewModalAllBins(false);
                                setSaveViewModalSelectedBinIds(new Set());
                                if (ok) setForceUpdate(prev => prev + 1);
                            }}
                            sx={{
                                backgroundColor: getThemeColor('#333', '#f4cc8e'),
                                color: getThemeColor('#fff', '#333'),
                            }}
                        >
                            Save
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>
        </>
    );
}

export default Bin3d;


//<Environment preset="dawn" blur={0.9} />